<script setup lang="ts">
/**
 * Under a customer's text bubble that contains a phone number (lib/phone.ts): "ผูกเบอร์ 085-883-6069 กับลูกค้า POS".
 * Click → exact POS lookup → 1 customer: confirm card · several: pick one · none: "สร้างลูกค้าใหม่" opens the order
 * tab's own create form with the number. Already linked to that number → a quiet "ผูกแล้ว"; linked to someone else →
 * "เปลี่ยนไปผูกเบอร์นี้" and the card says who it replaces. Store: stores/phonelink.ts. No overlay: the card sits in
 * the thread under the bubble; loading = a small spinner on the button.
 */
import { computed, ref, watchEffect } from 'vue'
import type { PosCustomer } from '../../api/pos/types'
import { detectPhones, type FoundPhone } from '../../lib/phone'
import { useInbox } from '../../stores/inbox'
import { usePhoneLink, type PhoneStatus } from '../../stores/phonelink'
import Icon from '../ui/Icon.vue'

const props = defineProps<{ convId: string; text: string | null }>()
const inbox = useInbox()
const pl = usePhoneLink()

const phones = computed<FoundPhone[]>(() => detectPhones(props.text))
const detail = computed(() => inbox.details[props.convId] ?? null)
const canCreate = computed(() => ['fb', 'ig', 'line'].includes(detail.value?.channel ?? ''))

watchEffect(() => {
  if (!phones.value.length || !detail.value || detail.value.kind !== 'dm' || detail.value.is_history) return
  pl.ensure(props.convId)
  for (const p of phones.value) if (pl.wantsLookup(props.convId, p.digits)) void pl.lookup(p.digits)
})

const rows = computed(() =>
  phones.value
    .map((p) => ({ p, st: pl.status(props.convId, p.digits) as PhoneStatus }))
    .filter((r) => r.st.kind !== 'hidden' && r.st.kind !== 'checking'),
)

const open = ref<string | null>(null) // digits of the card that is open
const loading = ref<string | null>(null)
const lookup = computed(() => (open.value ? (pl.lookups[open.value] ?? null) : null))
const busy = computed(() => !!pl.busy[props.convId])

async function press(p: FoundPhone): Promise<void> {
  if (open.value === p.digits) {
    open.value = null
    return
  }
  loading.value = p.digits
  try {
    await pl.lookup(p.digits)
    open.value = p.digits
  } finally {
    loading.value = null
  }
}

async function choose(c: PosCustomer): Promise<void> {
  if (await pl.link(props.convId, c)) open.value = null
}

function create(p: FoundPhone): void {
  open.value = null
  pl.createNew(props.convId, p.digits)
}

const TH_MONTHS = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']
/** POS date "2026-08-13 10:12:00" → "13 ส.ค. 69" */
function posDay(v: string | null): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v ?? '')
  if (!m) return ''
  return `${Number(m[3])}\u00a0${TH_MONTHS[Number(m[2]) - 1] ?? ''}\u00a0${(Number(m[1]) + 543) % 100}` // one piece: never "69" alone on a line
}
function customerKind(c: PosCustomer): string {
  return c.last_order_at ? `ลูกค้าเก่า · สั่งล่าสุด ${posDay(c.last_order_at)}` : 'ลูกค้าใหม่ · ยังไม่เคยสั่ง'
}
</script>

<template>
  <div v-if="rows.length" class="pl">
    <template v-for="{ p, st } in rows" :key="p.digits">
      <span v-if="st.kind === 'same'" class="pl-done" :title="`แชทนี้ผูกกับ ${st.name} (เบอร์ ${p.display}) อยู่แล้ว`">
        <Icon name="checkCircle" /><span>ผูกแล้ว · <span class="mono">{{ p.display }}</span><template v-if="st.name"> · {{ st.name }}</template></span>
      </span>
      <template v-else>
        <button
          type="button"
          class="pl-b"
          :class="{ on: open === p.digits }"
          :aria-expanded="open === p.digits"
          :disabled="loading !== null || busy"
          :title="st.kind === 'different' ? `แชทนี้ผูกกับ ${st.name} อยู่` : 'ค้นหาลูกค้าเบอร์นี้ใน POS แล้วผูกกับแชทนี้'"
          @click="press(p)"
        >
          <span v-if="loading === p.digits" class="pl-spin" aria-hidden="true"></span><Icon v-else name="link" />
          <span v-if="st.kind === 'different'">เปลี่ยนไปผูกเบอร์ <span class="mono">{{ p.display }}</span></span>
          <span v-else>ผูกเบอร์ <span class="mono">{{ p.display }}</span> กับลูกค้า POS</span>
        </button>

        <div v-if="open === p.digits && lookup" class="pl-card" role="group" :aria-label="`ลูกค้า POS เบอร์ ${p.display}`">
          <p v-if="st.kind === 'different'" class="pl-warn">
            <Icon name="alert" /><span>แชทนี้ผูกกับ <b>{{ st.name }}</b> อยู่ ถ้าผูกใหม่ แชทนี้จะเปลี่ยนไปเป็นลูกค้าที่เลือก</span>
          </p>

          <template v-if="lookup.state === 'error'">
            <p class="pl-err"><Icon name="alertCircle" /><span>ค้นหาใน POS ไม่สำเร็จ: {{ lookup.error }}</span></p>
            <div class="pl-acts">
              <button type="button" class="pl-x" @click="open = null">ปิด</button>
              <button type="button" class="pl-go" @click="pl.lookup(p.digits, { force: true })">ลองใหม่</button>
            </div>
          </template>

          <template v-else-if="lookup.matches.length === 1">
            <div class="pl-cus">
              <span class="pl-av" aria-hidden="true"><Icon name="user" /></span>
              <span class="pl-cb">
                <b>{{ lookup.matches[0]!.name }}</b>
                <span class="pl-sub"
                  ><span class="mono">{{ lookup.matches[0]!.mobile_masked ?? p.display }}</span> ·
                  {{ customerKind(lookup.matches[0]!) }}</span
                >
              </span>
            </div>
            <div class="pl-acts">
              <button type="button" class="pl-x" :disabled="busy" @click="open = null">ยกเลิก</button>
              <button type="button" class="pl-go" :disabled="busy" @click="choose(lookup.matches[0]!)">
                <span v-if="busy" class="pl-spin light" aria-hidden="true"></span><Icon v-else name="link" />{{
                  st.kind === 'different' ? 'เปลี่ยนไปผูกคนนี้' : 'ผูกแชทนี้'
                }}
              </button>
            </div>
          </template>

          <template v-else-if="lookup.matches.length > 1">
            <p class="pl-t">พบลูกค้า {{ lookup.matches.length }} คนที่ใช้เบอร์นี้ เลือกคนที่ใช่</p>
            <ul class="pl-list">
              <li v-for="c in lookup.matches.slice(0, 5)" :key="c.id" class="pl-row">
                <span class="pl-cb">
                  <b>{{ c.name }}</b>
                  <span class="pl-sub"><span class="mono">{{ c.mobile_masked ?? p.display }}</span> · {{ customerKind(c) }}</span>
                </span>
                <button type="button" class="pl-pick" :disabled="busy" :aria-label="`ผูกแชทนี้กับ ${c.name}`" @click="choose(c)">ผูก</button>
              </li>
            </ul>
            <div class="pl-acts">
              <button type="button" class="pl-x" :disabled="busy" @click="open = null">ยกเลิก</button>
            </div>
          </template>

          <template v-else>
            <p class="pl-t">ยังไม่มีลูกค้าเบอร์ <span class="mono">{{ p.display }}</span> ใน POS</p>
            <p v-if="!canCreate" class="pl-sub">แชทช่องทางนี้ยังเพิ่มลูกค้าใหม่จากหน้านี้ไม่ได้ เพิ่มที่ POS โดยตรง</p>
            <div class="pl-acts">
              <button type="button" class="pl-x" @click="open = null">ปิด</button>
              <button v-if="canCreate" type="button" class="pl-go" @click="create(p)"><Icon name="plus" />สร้างลูกค้าใหม่</button>
            </div>
          </template>
        </div>
      </template>
    </template>
  </div>
</template>

<style scoped>
.pl {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  max-width: 100%;
}
.pl-b {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 28px;
  max-width: 100%;
  padding: 3px 10px 3px 8px;
  border-radius: 8px;
  border: 1px solid #cfe3d8;
  background: #fff;
  color: var(--ac2);
  font-size: 12.5px;
  font-weight: 500;
  line-height: 19px;
  text-align: left;
}
.pl-b:hover:not(:disabled),
.pl-b.on {
  background: var(--acs);
}
.pl-b:disabled {
  cursor: default;
  opacity: 0.75;
}
.pl-b :deep(.ic) {
  flex: none;
  width: 14px;
  height: 14px;
  color: var(--ac);
}
.pl-done {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.pl-done :deep(.ic) {
  flex: none;
  width: 13px;
  height: 13px;
  color: var(--ac);
}
.mono {
  font-family: var(--mono);
  font-size: 12px;
}
.pl-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: min(340px, 100%);
  padding: 12px;
  border-radius: 12px;
  border: 1px solid var(--line);
  background: #fff;
  box-shadow: 0 4px 14px rgba(23, 33, 28, 0.08);
}
.pl-warn,
.pl-err {
  display: flex;
  gap: 6px;
  align-items: flex-start;
  margin: 0;
  font-size: 12.5px;
  line-height: 19px;
}
.pl-warn {
  padding: 8px 10px;
  border-radius: 8px;
  background: #fff8e6;
  color: #6b4300;
}
.pl-err {
  color: var(--crit);
}
.pl-warn :deep(.ic),
.pl-err :deep(.ic) {
  flex: none;
  width: 14px;
  height: 14px;
  margin-top: 2px;
}
.pl-t {
  margin: 0;
  font-size: 13px;
  line-height: 20px;
  font-weight: 500;
}
.pl-cus {
  display: flex;
  align-items: center;
  gap: 10px;
}
.pl-av {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--acs);
  color: var(--ac);
}
.pl-av :deep(.ic) {
  width: 16px;
  height: 16px;
}
.pl-cb {
  display: flex;
  flex-direction: column;
  min-width: 0;
  font-size: 13px;
  line-height: 20px;
}
.pl-cb b {
  font-weight: 600;
  overflow-wrap: anywhere;
}
.pl-sub {
  margin: 0;
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.pl-list {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid var(--line2);
  border-radius: 8px;
}
.pl-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
}
.pl-row + .pl-row {
  border-top: 1px solid var(--line2);
}
.pl-row .pl-cb {
  flex: 1;
}
.pl-pick {
  flex: none;
  height: 28px;
  padding: 0 12px;
  border-radius: 7px;
  border: 1px solid #cfe3d8;
  color: var(--ac2);
  font-size: 12.5px;
  font-weight: 600;
  line-height: 19px;
}
.pl-pick:hover:not(:disabled) {
  background: var(--acs);
}
.pl-acts {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.pl-x,
.pl-go {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 12px;
  border-radius: 8px;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 19px;
  white-space: nowrap;
}
.pl-x {
  border: 1px solid var(--line);
  background: #fff;
  color: var(--ink);
}
.pl-go {
  background: var(--ac);
  color: #fff;
}
.pl-go:hover:not(:disabled) {
  background: var(--ac2);
}
.pl-go :deep(.ic) {
  width: 14px;
  height: 14px;
}
button:disabled {
  opacity: 0.6;
  cursor: default;
}
.pl-spin {
  flex: none;
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid #cfe3d8;
  border-top-color: var(--ac);
  animation: pl-spin 0.8s linear infinite;
}
.pl-spin.light {
  border-color: rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
}
@keyframes pl-spin {
  to {
    transform: rotate(360deg);
  }
}
@media (prefers-reduced-motion: reduce) {
  .pl-spin {
    animation: none;
  }
}
</style>
